import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Search, MessageSquare, Clock, ChevronDown, ChevronUp,
  ExternalLink, Loader2, Users, LogIn, User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SEO } from '@/components/SEO';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { THREAD_CATEGORIES, THREAD_TAGS } from '@/data/forumData';
import { cn } from '@/lib/utils';
import { useForumThreads, useForumResponses } from '@/hooks/useForum';

function ReadOnlyResponses({ threadId }: { threadId: string }) {
  const { data: responses, isLoading } = useForumResponses(threadId);
  const navigate = useNavigate();

  return (
    <div className="mt-4 pt-4 border-t border-border">
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : responses && responses.length > 0 ? (
        <ScrollArea className="max-h-80">
          <div className="space-y-3 pr-2">
            {responses.map((response) => (
              <div
                key={response.id}
                className={cn(
                  'flex gap-3 p-3 rounded-lg',
                  response.is_solution ? 'bg-emerald-500/10 border border-emerald-200' : 'bg-muted/50',
                )}
              >
                <Avatar className="w-8 h-8 shrink-0">
                  <AvatarImage src={response.author?.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">
                    {response.author?.full_name
                      ? response.author.full_name.split(' ').map((n) => n[0]).join('')
                      : <User className="w-4 h-4 text-muted-foreground" />}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{response.author?.full_name || 'Anónimo'}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(parseISO(response.created_at), { addSuffix: true, locale: es })}
                    </span>
                    {response.is_solution && (
                      <Badge className="text-xs bg-emerald-500/20 text-emerald-600 border-emerald-200">✓ Útil</Badge>
                    )}
                  </div>
                  <p className="text-sm text-foreground mt-1">{response.content}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">Aún no hay comentarios</p>
      )}

      {/* CTA to register */}
      <div className="mt-4 p-4 rounded-lg bg-primary/5 border border-primary/20 text-center">
        <p className="text-sm text-muted-foreground mb-2">¿Quieres participar en la conversación?</p>
        <Button size="sm" onClick={() => navigate('/auth')} className="gap-2">
          <LogIn className="w-4 h-4" />
          Regístrate para comentar
        </Button>
      </div>
    </div>
  );
}

export default function PublicForumPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const threadParam = searchParams.get('thread');
  const { data: threads, isLoading } = useForumThreads();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(threadParam);

  useEffect(() => {
    if (threadParam && threads && threads.length > 0) {
      setExpandedThreadId(threadParam);
      setTimeout(() => {
        const el = document.getElementById(`thread-${threadParam}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [threadParam, threads]);

  const filteredThreads = (threads || []).filter((thread) => {
    const matchesSearch =
      searchQuery === '' ||
      thread.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (thread.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || thread.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredThreads.length / itemsPerPage));
  const paginatedThreads = filteredThreads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <SEO
        title="Comunidad y foro de IA — LÍDER IA"
        description="Únete a la conversación: hilos, preguntas y experiencias de profesionales aplicando IA en sus organizaciones."
        path="/forum"
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            <h1 className="text-3xl font-bold">Comunidad</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Explora las conversaciones de nuestra comunidad de líderes en IA
          </p>
        </div>
        <Button size="lg" onClick={() => navigate('/auth')} className="gap-2">
          <LogIn className="w-4 h-4" />
          Únete a la comunidad
        </Button>
      </div>

      {/* Search and Category Filter */}
      <div className="space-y-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar posts..." className="pl-10" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={categoryFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter('all')} className="rounded-full">Todos</Button>
          {Object.entries(THREAD_CATEGORIES).map(([key, { label, icon }]) => (
            <Button key={key} variant={categoryFilter === key ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter(key)} className="rounded-full gap-1.5">
              <span>{icon}</span>{label}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        <span>{filteredThreads.length} posts encontrados</span>
      </div>

      {/* Thread List */}
      <div className="space-y-3">
        <h2 className="sr-only">Discusiones de la comunidad</h2>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredThreads.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-medium">No se encontraron posts</h3>
              <p className="text-muted-foreground mt-1">
                {categoryFilter !== 'all' ? 'Intenta seleccionar otra categoría' : 'Aún no hay publicaciones en la comunidad'}
              </p>
            </CardContent>
          </Card>
        ) : (
          paginatedThreads.map((thread) => {
            const isExpanded = expandedThreadId === thread.id;
            const category = thread.category as keyof typeof THREAD_CATEGORIES;

            return (
              <Card key={thread.id} id={`thread-${thread.id}`} className={cn('transition-all duration-300', isExpanded && 'ring-2 ring-primary/20')}>
                <CardContent className="p-4">
                  <div className="flex gap-4 cursor-pointer" onClick={() => setExpandedThreadId(isExpanded ? null : thread.id)}>
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={thread.author?.avatar_url || undefined} />
                      <AvatarFallback>{thread.author?.full_name ? thread.author.full_name.split(' ').map((n) => n[0]).join('') : <User className="w-5 h-5 text-muted-foreground" />}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-2 sm:line-clamp-1">{thread.title}</h3>
                          <p className={cn('text-sm text-muted-foreground mt-1', !isExpanded && 'line-clamp-2')}>{thread.description}</p>
                          {thread.external_link && (
                            <a href={thread.external_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2">
                              <ExternalLink className="w-3.5 h-3.5" />Ver enlace externo
                            </a>
                          )}
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {THREAD_CATEGORIES[category] && (
                          <Badge variant="outline" className={cn('text-xs', THREAD_CATEGORIES[category].color)}>
                            {THREAD_CATEGORIES[category].icon} {THREAD_CATEGORIES[category].label}
                          </Badge>
                        )}
                        {(thread.tags || []).slice(0, 2).map((tag) => {
                          const tagKey = tag as keyof typeof THREAD_TAGS;
                          return (
                            <Badge key={tag} variant="outline" className={cn('text-xs', THREAD_TAGS[tagKey]?.color)}>
                              {THREAD_TAGS[tagKey]?.label || tag}
                            </Badge>
                          );
                        })}
                        <div className="flex items-center gap-3 ml-auto text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3.5 h-3.5" />{thread.response_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDistanceToNow(parseISO(thread.last_activity_at || thread.created_at), { addSuffix: true, locale: es })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isExpanded && <ReadOnlyResponses threadId={thread.id} />}
                </CardContent>
              </Card>
            );
          })
        )}

        {!isLoading && totalPages > 1 && (
          <Pagination className="pt-4">
            <PaginationContent className="flex-wrap">
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  const showEllipsis = prev && page - prev > 1;
                  return (
                    <span key={page} className="flex items-center">
                      {showEllipsis && <span className="px-2 text-muted-foreground">…</span>}
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    </span>
                  );
                })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      {/* Bottom CTA */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-8 text-center">
          <Users className="w-10 h-10 text-primary mx-auto mb-3" />
          <h3 className="text-xl font-semibold mb-2">¿Listo para participar?</h3>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            Crea una cuenta gratuita para publicar posts, comentar y colaborar con otros líderes en IA.
          </p>
          <Button size="lg" onClick={() => navigate('/auth')} className="gap-2">
            <LogIn className="w-4 h-4" />
            Crear cuenta gratis
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
